import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { Toaster } from "sonner";
import AdminDashboard from "./app/private/admin/admin.tsx";
import AdminAnalytics from "./app/private/admin/analytics.tsx";
import { PlanSyncScreen } from "./app/private/admin/components/plan-sync/plan-sync-screen.tsx";
import UsersPage from "./app/private/admin/users.tsx";
import { AI } from "./app/private/ai/ai.tsx";
import AuthLayout from "./app/public/auth/auth-layout.tsx";
import SignIn from "./app/public/auth/sign-in/sign-in.tsx";
import TwoFactor from "./app/public/auth/sign-in/two-factor/two-factor.tsx";
import SignUp from "./app/public/auth/sign-up/sign-up.tsx";
import StorefrontDiscovery from "./app/public/storefront/storefront-discovery.tsx";
import StorefrontLayout from "./app/public/storefront/storefront-layout.tsx";
import Billing from "./app/private/billing/billing.tsx";
import CheckoutConfirm from "./app/private/billing/checkout-confirm.tsx";
import CheckoutSuccess from "./app/private/billing/checkout-success.tsx";
import Pricing from "./app/private/billing/pricing.tsx";
import SubscriptionWelcome from "./app/private/billing/subscription-welcome.tsx";
import Home from "./app/private/home/home.tsx";
import CollectionFormPage from "./app/private/menu/collection-form.tsx";
import MenuCollectionsPage from "./app/private/menu/collections.tsx";
import ProductFormPage from "./app/private/menu/product-form.tsx";
import MenuBoardPage from "./app/private/menu/board.tsx";
import MenuProductsPage from "./app/private/menu/products.tsx";
import ProductsViewPage from "./app/private/menu/products-view.tsx";
import PromotionsPage from "./app/private/menu/promotions/promotions.tsx";
import CollectionPromotionFormPage from "./app/private/menu/promotions/collection-promotion-form.tsx";
import CollectionPromotionsPage from "./app/private/menu/promotions/collection-promotions.tsx";
import CustomPromotionFormPage from "./app/private/menu/promotions/custom-promotion-form.tsx";
import CustomPromotionsPage from "./app/private/menu/promotions/custom-promotions.tsx";
import SectionPromotionFormPage from "./app/private/menu/promotions/section-promotion-form.tsx";
import SectionPromotionsPage from "./app/private/menu/promotions/section-promotions.tsx";
import AcceptInvitation from "./app/public/accept-invitation/accept-invitation.tsx";
import BoardScreenPage from "./app/public/board/board-screen.tsx";
import OrdersKitchenBoard from "./app/public/board/orders-kitchen.tsx";
import OrdersPickupBoard from "./app/public/board/orders-pickup.tsx";
import PublicMenuFlowPage from "./app/public/menu-flow/public-menu-flow-page.tsx";
import CheckoutPage from "./app/public/orders/checkout.tsx";
import OrderStatusPage from "./app/public/orders/order-status.tsx";
import StaffOrderDetail from "./app/private/orders/order-detail.tsx";
import StaffOrdersPage from "./app/private/orders/orders.tsx";
import OrderingSettingsPage from "./app/private/orders/ordering-settings.tsx";
import RidersPage from "./app/private/orders/riders.tsx";
import StaffTablesPage from "./app/private/orders/tables.tsx";
import AccountLayout from "./app/account/account-layout.tsx";
import AddressesListPage from "./app/account/addresses/addresses-list.tsx";
import AddressFormPage from "./app/account/addresses/address-form.tsx";
import OrderHistoryListPage from "./app/account/orders/orders-list.tsx";
import OrderReceiptPage from "./app/account/orders/order-receipt.tsx";
import PhoneVerifyPage from "./app/account/phone-verify.tsx";
import DeliveryCheckoutPage from "./app/public/delivery/delivery-checkout.tsx";
import DeliveryOrderStatusPage from "./app/public/delivery/delivery-order-status.tsx";
import DeliveryTrackingPage from "./app/public/delivery/delivery-tracking.tsx";
import RiderShell from "./app/rider/rider-shell.tsx";
import RiderQueuePage from "./app/rider/rider-queue.tsx";
import RiderHistoryPage from "./app/rider/rider-history.tsx";
import RiderDeliveryDetailPage from "./app/rider/rider-delivery-detail.tsx";
import SectionFormPage from "./app/private/menu/section-form.tsx";
import MenuSectionsPage from "./app/private/menu/sections.tsx";
import OrganizationsPage from "./app/private/organizations/organizations.tsx";
import { Settings } from "./app/private/settings/settings.tsx";
import { AdminTierOverrideBanner } from "./components/admin-tier-override-banner.tsx";
import { AdminRoute } from "./components/admin-route.tsx";
import { BillingManagerRoute } from "./components/billing-manager-route.tsx";
import { MainLayout } from "./components/main-layout.tsx";
import { OrgAdminRoute } from "./components/org-admin-route.tsx";
import { PaymentProviderWrapper } from "./components/payment-provider-wrapper.tsx";
import { ProtectedRoute } from "./components/protected-route.tsx";
import { PublicRoute } from "./components/public-route.tsx";
import { RiderRoute } from "./components/rider-route.tsx";
import { MembershipProvider } from "./contexts/membership-context.tsx";
import { ThemeProvider } from "./contexts/theme-context.tsx";

// Constants
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const DEFAULT_STALE_TIME_MS = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND; // 60 seconds in milliseconds

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: DEFAULT_STALE_TIME_MS,
      },
    },
  });
}
let browserQueryClient: QueryClient | undefined;
function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

function App() {
  const queryClient = getQueryClient();

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route
              element={
                <PublicRoute>
                  <AuthLayout />
                </PublicRoute>
              }
              path="auth"
            >
              <Route element={<SignIn accountType="restaurant" />} path="sign-in" />
              <Route element={<TwoFactor />} path="sign-in/two-factor" />
              <Route element={<SignUp accountType="restaurant" />} path="sign-up" />
              <Route element={<SignIn accountType="restaurant" />} path="restaurant/sign-in" />
              <Route element={<TwoFactor />} path="restaurant/sign-in/two-factor" />
              <Route element={<SignUp accountType="restaurant" />} path="restaurant/sign-up" />
              <Route element={<SignIn accountType="consumer" />} path="consumer/sign-in" />
              <Route element={<TwoFactor />} path="consumer/sign-in/two-factor" />
              <Route element={<SignUp accountType="consumer" />} path="consumer/sign-up" />
              <Route element={<Navigate replace to="sign-in" />} index />
            </Route>
            <Route element={<PublicMenuFlowPage />} path="m/:orgSlug" />
            <Route element={<CheckoutPage />} path="m/:orgSlug/checkout" />
            <Route element={<OrderStatusPage />} path="m/:orgSlug/order/:id" />
            <Route element={<DeliveryCheckoutPage />} path="m/:orgSlug/delivery/checkout" />
            <Route element={<DeliveryOrderStatusPage />} path="m/:orgSlug/delivery/order/:id" />
            <Route element={<DeliveryTrackingPage />} path="m/:orgSlug/delivery/order/:id/track" />
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["consumer"]}
                  signInPath="/auth/consumer/sign-in"
                  unauthorizedPath="/"
                >
                  <AccountLayout />
                </ProtectedRoute>
              }
              path="account"
            >
              <Route element={<Navigate replace to="addresses" />} index />
              <Route element={<AddressesListPage />} path="addresses" />
              <Route element={<AddressFormPage />} path="addresses/new" />
              <Route element={<AddressFormPage />} path="addresses/:id" />
              <Route element={<OrderHistoryListPage />} path="orders" />
              <Route element={<OrderReceiptPage />} path="orders/:id" />
              <Route element={<PhoneVerifyPage />} path="phone-verify" />
            </Route>
            <Route
              element={
                <RiderRoute>
                  <RiderShell />
                </RiderRoute>
              }
              path="rider"
            >
              <Route element={<RiderQueuePage />} index />
              <Route element={<RiderHistoryPage />} path="history" />
              <Route element={<RiderDeliveryDetailPage />} path="deliveries/:id" />
            </Route>
            <Route element={<BoardScreenPage />} path="b/:orgSlug/:screenIndex" />
            <Route element={<OrdersKitchenBoard />} path="b/:orgSlug/orders/kitchen" />
            <Route element={<OrdersPickupBoard />} path="b/:orgSlug/orders/pickup" />
            <Route element={<AcceptInvitation />} path="accept-invitation/:id" />

            <Route element={<StorefrontLayout />} path="/">
              <Route element={<StorefrontDiscovery />} index />
            </Route>

            {/* Todas las demás rutas con providers */}
            <Route
              element={
                <PaymentProviderWrapper>
                  <MembershipProvider>
                    <AdminTierOverrideBanner />
                    <Routes>
                      <Route
                        element={
                          <ProtectedRoute
                            allowedRoles={["admin", "user"]}
                            signInPath="/auth/restaurant/sign-in"
                            unauthorizedPath="/account"
                          >
                            <MainLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route element={<Home />} path="dashboard" />
                        <Route element={<Settings />} path="settings" />
                        <Route element={<MenuSectionsPage />} path="menu/sections" />
                        <Route element={<SectionFormPage />} path="menu/sections/new" />
                        <Route element={<SectionFormPage />} path="menu/sections/:id/edit" />
                        <Route element={<MenuCollectionsPage />} path="menu/collections" />
                        <Route element={<CollectionFormPage />} path="menu/collections/new" />
                        <Route element={<CollectionFormPage />} path="menu/collections/:id/edit" />
                        <Route element={<MenuProductsPage />} path="menu/products" />
                        <Route element={<ProductFormPage />} path="menu/products/new" />
                        <Route element={<ProductFormPage />} path="menu/products/:id/edit" />
                        <Route element={<PromotionsPage />} path="menu/promotions" />
                        <Route element={<CustomPromotionsPage />} path="menu/promotions/custom" />
                        <Route
                          element={<CustomPromotionFormPage />}
                          path="menu/promotions/custom/new"
                        />
                        <Route
                          element={<CustomPromotionFormPage />}
                          path="menu/promotions/custom/:id/edit"
                        />
                        <Route element={<SectionPromotionsPage />} path="menu/promotions/section" />
                        <Route
                          element={<SectionPromotionFormPage />}
                          path="menu/promotions/section/new"
                        />
                        <Route
                          element={<SectionPromotionFormPage />}
                          path="menu/promotions/section/:id/edit"
                        />
                        <Route
                          element={<CollectionPromotionsPage />}
                          path="menu/promotions/collection"
                        />
                        <Route
                          element={<CollectionPromotionFormPage />}
                          path="menu/promotions/collection/new"
                        />
                        <Route
                          element={<CollectionPromotionFormPage />}
                          path="menu/promotions/collection/:id/edit"
                        />
                        <Route element={<ProductsViewPage />} path="menu/products-view" />
                        <Route element={<MenuBoardPage />} path="menu/board" />
                        <Route element={<StaffTablesPage />} path="menu/tables" />
                        <Route element={<StaffOrdersPage />} path="orders" />
                        <Route element={<StaffOrderDetail />} path="orders/:id" />
                        <Route
                          element={
                            <OrgAdminRoute>
                              <OrderingSettingsPage />
                            </OrgAdminRoute>
                          }
                          path="orders/settings"
                        />
                        <Route
                          element={
                            <OrgAdminRoute>
                              <RidersPage />
                            </OrgAdminRoute>
                          }
                          path="orders/riders"
                        />
                        <Route
                          element={
                            <OrgAdminRoute>
                              <OrganizationsPage />
                            </OrgAdminRoute>
                          }
                          path="organizations"
                        />
                        <Route
                          element={
                            <BillingManagerRoute>
                              <Billing />
                            </BillingManagerRoute>
                          }
                          path="billing"
                        />
                      </Route>

                      <Route
                        element={
                          <AdminRoute>
                            <MainLayout />
                          </AdminRoute>
                        }
                        path="admin"
                      >
                        <Route element={<AdminDashboard />} index />
                        <Route element={<AdminAnalytics />} path="analytics" />
                        <Route element={<PlanSyncScreen />} path="plans" />
                        <Route element={<UsersPage />} path="users" />
                      </Route>

                      <Route element={<Pricing />} path="pricing" />
                      <Route element={<CheckoutConfirm />} path="checkout-confirm" />
                      <Route element={<CheckoutSuccess />} path="checkout-success" />
                      <Route element={<SubscriptionWelcome />} path="subscription-welcome" />
                      <Route element={<AI />} path="ai" />

                      {/* Redirect any unknown routes to home */}
                      <Route element={<Navigate replace to="/" />} path="*" />
                    </Routes>
                    <Toaster />
                  </MembershipProvider>
                </PaymentProviderWrapper>
              }
              path="/*"
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
